/**
 * Requêtes API pour les données web d'un proposal.
 *
 * Deux types de requêtes :
 *
 *  • Admin (JWT admin via instance Axios partagée) :
 *      GET/POST  /proposal/:id/web_data
 *      POST      /proposal/:id/publish_web
 *      GET       /proposal/:id/web_accesses
 *      DELETE    /proposal/:id/web_accesses/:access_id
 *      GET       /proposal/:id/web_validation
 *      POST      /proposal/:id/web_validation
 *      DELETE    /proposal/:id/web_validation
 *
 *  • Viewer (fetch direct avec JWT viewer ou sans auth) :
 *      GET  /proposals/:id/login_page              → public
 *      POST /web_viewer/login                      → public → JWT identité
 *      GET  /web_viewer/sites                      → JWT identité
 *      POST /web_viewer/sites/:proposal_id/access  → JWT identité → JWT accès site
 *      GET  /web_viewer/sites/:proposal_id/content → JWT accès site
 */

import { instance } from '../../requests/axios.instance';
import { normalizeWebValidation } from '../utils/publishGate';

// URL de base de l'API Rails (ex: http://localhost:3000/api)
const API_BASE = process.env.REACT_APP_API_URL || '/api';

// ── Helpers fetch viewer (tokens JWT non-admin) ────────────────────────────────

async function viewerFetch(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options,
  });
  return res;
}

// ── Routes publiques (viewer) ─────────────────────────────────────────────────

/**
 * Responsable de traitement du site et ses contacts, tels que le serveur les
 * résout (Proposals::PrivacyNotice, dépôt web). `null` si absent.
 */
export function normalizePrivacy(raw) {
  if (!raw) return null;
  const controller = raw.controller;
  return {
    controller: controller ? {
      entityId: controller.entity_id,
      name: controller.name,
      legalForm: controller.legal_form || null,
      siret: controller.siret || null,
      address: controller.address || null,
      email: controller.email || null,
    } : null,
    dpo: raw.dpo || null,
    policyUrl: raw.policy_url || null,
    processor: raw.processor || null,
    readingTrackingAvailable: raw.reading_tracking_available === true,
  };
}

/**
 * Récupère les données de la page de login (sans auth).
 * GET /proposals/:id/login_page
 * Retourne { login_page_puck_data, theme } ou null si non publié (404).
 */
export async function fetchLoginPageData(proposalId) {
  const res = await viewerFetch(`/proposals/${proposalId}/login_page`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Impossible de charger la page de connexion');
  return res.json();
}

/**
 * Authentifie un WebUser → JWT identité.
 * POST /web_viewer/login
 */
export async function viewerLogin(email, password) {
  const res = await viewerFetch('/web_viewer/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  const body = await res.json();
  if (!res.ok) throw new Error(body.error || 'Identifiants invalides');
  return body; // { token }
}

/**
 * Liste les sites actifs de l'utilisateur (JWT identité requis).
 * GET /web_viewer/sites
 */
export async function fetchAccessibleSites(identityToken) {
  const res = await viewerFetch('/web_viewer/sites', {
    headers: { Authorization: `Bearer ${identityToken}` },
  });
  if (!res.ok) throw new Error('Impossible de charger la liste des sites');
  return res.json();
}

/**
 * Demande un JWT accès à un site spécifique (JWT identité requis).
 * POST /web_viewer/sites/:proposal_id/access
 * Retourne { token, expires_at }.
 */
export async function requestSiteAccess(identityToken, proposalId) {
  const res = await viewerFetch(`/web_viewer/sites/${proposalId}/access`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${identityToken}` },
  });
  const body = await res.json();
  if (res.status === 403) {
    const err = new Error(body.error || 'Accès refusé');
    err.expired = body.expired || false;
    throw err;
  }
  if (!res.ok) throw new Error(body.error || "Impossible d'accéder à ce site");
  return body; // { token, expires_at }
}

/**
 * Charge le contenu du site (JWT accès site requis).
 * GET /web_viewer/sites/:id/content
 * Retourne { web_data, web_template: { theme, header_puck_data, footer_puck_data } }
 */
export async function fetchWebContent(proposalId, siteAccessToken) {
  const res = await viewerFetch(`/web_viewer/sites/${proposalId}/content`, {
    headers: { Authorization: `Bearer ${siteAccessToken}` },
  });
  if (res.status === 401) {
    const err = new Error('Non autorisé');
    err.status = 401;
    throw err;
  }
  if (res.status === 403) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.error || 'Accès expiré');
    err.expired = true;
    throw err;
  }
  if (!res.ok) throw new Error('Impossible de charger le contenu du site');
  return res.json();
}

// ── Routes admin (JWT admin via instance Axios) ───────────────────────────────

/** Charge les web_data d'un proposal avec le jeton de version du serveur.
 *  Le lire ici évite au premier enregistrement de se faire refuser pour
 *  découvrir la version. */
export async function fetchWebDataWithVersion(proposalId) {
  try {
    const res = await instance.get(`/proposal/${proposalId}/web_data`);
    // Le backend retourne { web_data: {...}, web_template_id, is_web_published,
    // version, web_validation }
    return {
      webData: res.data?.web_data || res.data || null,
      webTemplateId: res.data?.web_template_id ?? null,
      kind: res.data?.kind || null,
      version: res.data?.version ?? null,
      scopeVersions: res.data?.scope_versions || {},
      webValidation: normalizeWebValidation(res.data?.web_validation),
    };
  } catch (e) {
    if (e.response?.status === 404) {
      return {
        webData: null,
        version: null,
        scopeVersions: {},
        webValidation: normalizeWebValidation(null),
      };
    }
    // Propager le status : les apps distinguent 401/403 (accès) des autres erreurs
    const err = new Error('Impossible de charger les données web');
    err.status = e.response?.status;
    throw err;
  }
}

/** Contenu seul, pour les écrans qui ne sauvegardent pas (aperçu, bouton de
 *  génération). */
export async function fetchWebData(proposalId) {
  const { webData } = await fetchWebDataWithVersion(proposalId);
  return webData;
}

/** Sauvegarde les web_data. `version` = jeton de conflit du serveur : il refuse
 *  l'écriture (409) si le brouillon a bougé depuis la dernière réponse. */
export async function saveWebData(proposalId, data, version) {
  const res = await instance.post(`/proposal/${proposalId}/web_data`, {
    web_data: data,
    ...(version != null ? { version } : {}),
  });
  return res.data;
}

/** Écriture granulaire : seul le bloc modifié est réécrit côté serveur.
 *  `scope` = "page:<slug>", `version` = jeton DE CE SCOPE. */
export async function patchWebBlocks(proposalId, { scope, diff, version }) {
  const res = await instance.patch(`/proposal/${proposalId}/web_data/blocks`, {
    scope,
    diff,
    ...(version != null ? { version } : {}),
  });
  return res.data;
}

/** Structure du site : pages, ordre, projectConfig — jamais le puckData.
 *  `version` = jeton du scope réservé de structure. */
export async function patchWebStructure(proposalId, { diff, version }) {
  const res = await instance.patch(`/proposal/${proposalId}/web_data/structure`, {
    diff,
    ...(version != null ? { version } : {}),
  });
  return res.data;
}

/** Charge le proposal (contenu brut pour le parser) */
export async function fetchProposal(proposalId) {
  const res = await instance.get(`/proposal/${proposalId}`);
  return res.data;
}

/* Les variables ne se chargent plus ici : `/proposals/opportunities/:id/variables`
   attendait un identifiant d'opportunité alors que l'éditeur web manipule un
   identifiant de proposal (404 silencieuse, variables jamais renseignées). Le
   catalogue passe désormais par `getVariables({ id, type })`
   (`src/requests/documentDatas.js`), piloté par `utils/variableCatalogRegistry`. */

/** Publie le site (emails + date péremption) */
export async function publishWebSite(proposalId, { emails, expiresAt }) {
  const res = await instance.post(`/proposal/${proposalId}/publish_web`, {
    emails,
    expires_at: expiresAt,
  });
  return res.data;
}

/** État de validation seul — relu au retour sur l'onglet, quand le message
 *  collab a pu être manqué (session ouverte hors ligne, canal indisponible). */
export async function fetchWebValidation(proposalId) {
  const res = await instance.get(`/proposal/${proposalId}/web_validation`);
  return normalizeWebValidation(res.data?.web_validation || res.data);
}

/** Valide le site d'un document : les non-admins peuvent le publier.
 *  Réservé aux administrateurs (le serveur tranche). */
export async function validateWebSite(proposalId) {
  const res = await instance.post(`/proposal/${proposalId}/web_validation`);
  return normalizeWebValidation(res.data?.web_validation || res.data);
}

/** Retire la validation — le serveur dépublie le site dans la foulée. */
export async function unvalidateWebSite(proposalId) {
  const res = await instance.delete(`/proposal/${proposalId}/web_validation`);
  return normalizeWebValidation(res.data?.web_validation || res.data);
}

/** Révoque un accès utilisateur */
export async function revokeAccess(proposalId, accessId) {
  await instance.delete(`/proposal/${proposalId}/web_accesses/${accessId}`);
}

/** Liste les accès existants.
 *
 *  Le serveur répond en snake_case et renvoie **aussi les accès révoqués** —
 *  révoquer met `is_revoked` à vrai, ça n'efface pas la ligne. La conversion se
 *  fait ici : lue en camelCase sans être convertie, `expires_at` restait
 *  invisible (colonne « Expire le » vide) et un accès révoqué se relisait comme
 *  actif à la réouverture de la modale. */
/**
 * Les lecteurs pourront-ils joindre la société pour leurs données (e-mail ou
 * adresse) ? Un échec ne doit pas faire croire à un manque : on suppose oui.
 */
export async function fetchWebPrivacyStatus(proposalId) {
  try {
    const res = await instance.get(`/proposal/${proposalId}/web_privacy`);
    return { contactAvailable: res.data?.contact_available !== false };
  } catch {
    return { contactAvailable: true };
  }
}

export async function fetchWebAccesses(proposalId) {
  try {
    const res = await instance.get(`/proposal/${proposalId}/web_accesses`);
    return (res.data || []).map((access) => ({
      id: access.id,
      email: access.email,
      firstName: access.first_name,
      lastName: access.last_name,
      expiresAt: access.expires_at,
      isRevoked: !!access.is_revoked,
      expired: !!access.expired,
      lastAccessedAt: access.last_accessed_at,
    }));
  } catch {
    return [];
  }
}
